/**
 * @fileoverview ToggleGroup Component
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @based-on shadcn/ui + Figma design specification
 */

import { createContext, useContext, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

export type ToggleGroupVariant = 'default' | 'outline';
export type ToggleGroupSize = 'small' | 'regular' | 'large';
export type ToggleGroupType = 'single' | 'multiple';

export interface ToggleGroupProps {
  type?: ToggleGroupType;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  variant?: ToggleGroupVariant;
  size?: ToggleGroupSize;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

export interface ToggleGroupItemProps {
  value: string;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

interface ToggleGroupContextValue {
  type: ToggleGroupType;
  value: string | string[];
  onValueChange: (itemValue: string) => void;
  variant: ToggleGroupVariant;
  size: ToggleGroupSize;
  disabled: boolean;
}

const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);

const toggleGroupItemVariants = cva(
  'inline-flex items-center justify-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-transparent hover:bg-accent-1 data-[state=on]:bg-accent-1 data-[state=on]:text-foreground',
        outline: 'border border-transparent hover:bg-accent-1 data-[state=on]:border-border data-[state=on]:bg-card',
      },
      size: {
        small: 'h-8 px-2.5 [&>svg]:w-4 [&>svg]:h-4',
        regular: 'h-10 px-3 [&>svg]:w-4 [&>svg]:h-4',
        large: 'h-12 px-4 [&>svg]:w-5 [&>svg]:h-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'regular',
    },
  }
);

export function ToggleGroup({
  type = 'single',
  value,
  onValueChange,
  variant = 'default',
  size = 'regular',
  disabled = false,
  children,
  className,
}: ToggleGroupProps) {
  const handleValueChange = (itemValue: string) => {
    if (type === 'single') {
      onValueChange?.(itemValue);
    } else {
      const currentValues = (value as string[]) || [];
      const newValues = currentValues.includes(itemValue)
        ? currentValues.filter((v) => v !== itemValue)
        : [...currentValues, itemValue];
      onValueChange?.(newValues);
    }
  };

  return (
    <ToggleGroupContext.Provider
      value={{
        type,
        value: value || (type === 'single' ? '' : []),
        onValueChange: handleValueChange,
        variant,
        size,
        disabled,
      }}
    >
      <div
        role="group"
        className={cn(
          'inline-flex items-center rounded-md border border-accent-2 bg-accent-0 p-1',
          className
        )}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

export function ToggleGroupItem({ value, disabled, children, className }: ToggleGroupItemProps) {
  const context = useContext(ToggleGroupContext);
  if (!context) throw new Error('ToggleGroupItem must be used within ToggleGroup');

  const isOn = context.type === 'single'
    ? context.value === value
    : (context.value as string[]).includes(value);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isOn}
      data-state={isOn ? 'on' : 'off'}
      disabled={disabled || context.disabled}
      onClick={() => context.onValueChange(value)}
      className={cn(
        toggleGroupItemVariants({ variant: context.variant, size: context.size }),
        'rounded-sm',
        className
      )}
    >
      {children}
    </button>
  );
}
